import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Divider, Chip, Alert, IconButton } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSelector } from 'react-redux';
import CommonDrawerLayout from '../../../common_components/CommonDrawerLayout';
import AppButton from '../../../common_components/AppButton';
import { NumberInput, Dropdown, FormRow } from '../../../common_components/FormInputs';
import CommonLoader from '../../../common_components/CommonLoader';
import { toast } from '../../../common_components/Toast/ToastUtils';
import metaAdAuditService from '../../../services/metaAdAudit.service';
function fromEditable(row) {
    const num = (v) => {
        const t = v.trim();
        if (!t)
            return null;
        const n = Number(t);
        return Number.isFinite(n) ? n : null;
    };
    return {
        range: row.range,
        ocpc: num(row.ocpc),
        octr: num(row.octr),
        roas: num(row.roas),
    };
}
function prettyRange(w) {
    const suffix = w.windowDays ? ` (${w.windowDays}d)` : '';
    return `${w.windowStart} → ${w.windowEnd}${suffix}`;
}
const CustomBenchmarkDrawer = ({ open, onClose, reportId, onSaved, disabled, }) => {
    const authToken = useSelector((s) => s?.auth?.token);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [missing, setMissing] = useState([]);
    const [all, setAll] = useState([]);
    const [existingCustom, setExistingCustom] = useState([]);
    const [reportWindowDays, setReportWindowDays] = useState(30);
    const [rows, setRows] = useState([]);
    const [errors, setErrors] = useState({});
    // Controlled selections for the two dropdowns (cleared after "Add")
    const [selectedMissing, setSelectedMissing] = useState('');
    const [selectedOverride, setSelectedOverride] = useState('');
    // Load both lists when the drawer opens
    useEffect(() => {
        if (!open || !reportId)
            return;
        let cancelled = false;
        setLoading(true);
        setErrors({});
        metaAdAuditService
            .getBenchmarkWindows(reportId, authToken || '')
            .then((res) => {
            if (cancelled)
                return;
            if (res?.status === 200 && res?.data?.success) {
                const data = res.data.data || {};
                setMissing(data.missing || []);
                setAll(data.all || []);
                setExistingCustom(data.existing_custom || []);
                setReportWindowDays(data.benchmark_window_days || 30);
                // Pre-populate rows with any previously saved custom
                // overrides so the user sees them without having to
                // re-select from the Override dropdown.
                const safeVal = (v) => {
                    if (v == null)
                        return '';
                    if (typeof v === 'object') {
                        if (v.value !== undefined)
                            return String(v.value);
                        const s = String(v);
                        return s === '[object Object]' ? '' : s;
                    }
                    return String(v);
                };
                setRows((data.existing_custom || []).map((c) => {
                    const match = (data.all || []).find((w) => w.range === c.range);
                    return {
                        range: c.range,
                        windowStart: match?.windowStart ?? c.range.split('→')[0] ?? '',
                        windowEnd: match?.windowEnd ?? c.range.split('→')[1] ?? '',
                        windowDays: match?.windowDays ?? null,
                        ocpc: safeVal(c.ocpc),
                        octr: safeVal(c.octr),
                        roas: safeVal(c.roas),
                        origin: 'saved',
                    };
                }));
            }
            else {
                toast.error(res?.data?.message || 'Failed to load benchmark windows');
            }
        })
            .catch((err) => {
            if (!cancelled) {
                toast.error(err?.message || 'Failed to load benchmark windows');
            }
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [open, reportId, authToken]);
    const rowRangesSet = useMemo(() => new Set(rows.map((r) => r.range)), [rows]);
    // Dropdown 1 options — missing only; hide ranges already in rows
    const missingOptions = useMemo(() => missing
        .filter((m) => !rowRangesSet.has(m.range))
        .map((m) => ({
        value: m.range,
        label: `${prettyRange(m)} · ${m.adCount} ad${m.adCount === 1 ? '' : 's'}`,
    })), [missing, rowRangesSet]);
    // Dropdown 2 options — every range; mark which are custom / missing
    const allOptions = useMemo(() => all
        .filter((w) => !rowRangesSet.has(w.range))
        .map((w) => {
        const badge = w.isCustom
            ? ' · custom'
            : w.isMissing
                ? ' · missing'
                : '';
        return {
            value: w.range,
            label: `${prettyRange(w)} · ${w.adCount} ad${w.adCount === 1 ? '' : 's'}${badge}`,
        };
    }), [all, rowRangesSet]);
    const handleAddMissing = (range) => {
        if (!range)
            return;
        const m = missing.find((x) => x.range === range);
        if (!m)
            return;
        setRows((prev) => [
            ...prev,
            {
                range: m.range,
                windowStart: m.windowStart,
                windowEnd: m.windowEnd,
                windowDays: m.windowDays,
                ocpc: '',
                octr: '',
                roas: '',
                origin: 'missing',
            },
        ]);
        setSelectedMissing('');
    };
    const handleAddOverride = (range) => {
        if (!range)
            return;
        const w = all.find((x) => x.range === range);
        if (!w)
            return;
        // Pre-fill with saved custom values if present, else auto values
        const saved = existingCustom.find((c) => c.range === range);
        const safeVal = (v) => {
            if (v == null)
                return '';
            if (typeof v === 'object') {
                if (v.value !== undefined)
                    return String(v.value);
                const s = String(v);
                return s === '[object Object]' ? '' : s;
            }
            return String(v);
        };
        const safeFixed = (v) => {
            if (v == null)
                return '';
            if (typeof v === 'number')
                return v.toFixed(2);
            if (typeof v === 'object' && v.value !== undefined)
                return Number(v.value).toFixed(2);
            return '';
        };
        setRows((prev) => [
            ...prev,
            {
                range: w.range,
                windowStart: w.windowStart,
                windowEnd: w.windowEnd,
                windowDays: w.windowDays,
                ocpc: saved?.ocpc != null
                    ? safeVal(saved.ocpc)
                    : w.bench.ocpc != null
                        ? safeFixed(w.bench.ocpc)
                        : '',
                octr: saved?.octr != null
                    ? safeVal(saved.octr)
                    : w.bench.octr != null
                        ? safeFixed(w.bench.octr)
                        : '',
                roas: saved?.roas != null
                    ? safeVal(saved.roas)
                    : w.bench.roas != null
                        ? safeFixed(w.bench.roas)
                        : '',
                origin: 'override',
            },
        ]);
        setSelectedOverride('');
    };
    const handleUpdateRow = (idx, patch) => {
        setRows((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], ...patch };
            return next;
        });
    };
    const handleRemoveRow = (idx) => {
        setRows((prev) => prev.filter((_, i) => i !== idx));
        setErrors((prev) => {
            const next = { ...prev };
            delete next[idx];
            return next;
        });
    };
    const validate = () => {
        const errs = {};
        rows.forEach((r, i) => {
            const e = fromEditable(r);
            if (e.ocpc == null && e.octr == null && e.roas == null) {
                errs[i] = 'Provide at least one of OCPC / OCTR / ROAS';
                return;
            }
            if (e.ocpc != null && e.ocpc < 0)
                errs[i] = 'OCPC must be ≥ 0';
            if (e.octr != null && (e.octr < 0 || e.octr > 100))
                errs[i] = 'OCTR is a percent (0-100)';
            if (e.roas != null && e.roas < 0)
                errs[i] = 'ROAS must be ≥ 0';
        });
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };
    const handleSave = async () => {
        if (!validate()) {
            toast.error('Fix the highlighted rows before saving.');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                custom_benchmarks: rows.map(fromEditable),
            };
            const res = await metaAdAuditService.saveCustomBenchmarks(reportId, payload, authToken || '');
            if (res?.status === 200 || res?.status === 202) {
                const queued = res?.status === 202;
                toast.success(queued
                    ? 'Saved. Audit is re-running in the background.'
                    : 'Saved. Audit re-run with new benchmarks.');
                onSaved();
                onClose();
            }
            else {
                toast.error(res?.data?.message || 'Failed to save benchmarks');
            }
        }
        catch (err) {
            toast.error(err?.message || 'Failed to save benchmarks');
        }
        finally {
            setSaving(false);
        }
    };
    return (_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: "Custom Benchmarks", width: "md", headerActions: _jsx(AppButton, { variant: "contained", onClick: handleSave, disabled: saving || loading || disabled || rows.length === 0, sx: {
                borderRadius: '8px',
                px: 3,
                bgcolor: '#FF008E',
                '&:hover': { bgcolor: '#E6007A' },
                textTransform: 'none',
                fontWeight: 600,
            }, children: saving ? 'Saving…' : 'Save & Re-run' }), children: _jsx(Box, { sx: { p: 1, bgcolor: '#F9FAFB', minHeight: '100%', position: 'relative' }, children: loading ? (_jsx(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: 2
                }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : (_jsxs(_Fragment, { children: [_jsxs(Alert, { severity: "info", sx: {
                            mb: 2,
                            bgcolor: '#F0F9FF',
                            border: '1px solid #BAE6FD',
                            '& .MuiAlert-icon': { color: '#0369A1' },
                        }, children: [_jsx(Typography, { sx: { fontSize: 12.5, color: '#075985', fontWeight: 600, mb: 0.5 }, children: "How benchmarks work" }), _jsxs(Typography, { sx: { fontSize: 11.5, color: '#075985', lineHeight: 1.6 }, children: ["Each ad's benchmark is the", ' ', _jsxs("strong", { children: [reportWindowDays, "-day"] }), " window ending the day before the ad's anchor date (the 1st or 15th of its start month \u2014 whichever comes first). Use the dropdowns below to add overrides for windows where the auto benchmark is wrong or missing."] })] }), _jsx(Box, { sx: {
                            bgcolor: '#fff',
                            border: '1px solid #EAECF0',
                            borderRadius: '8px',
                            p: 2,
                            mb: 2,
                        }, children: _jsxs(FormRow, { columns: 2, gap: 16, children: [_jsxs(Box, { children: [_jsx(Dropdown, { label: `Add missing benchmark (${missingOptions.length})`, value: selectedMissing, onChange: (v) => {
                                                const val = String(v ?? '');
                                                setSelectedMissing(val);
                                                if (val)
                                                    handleAddMissing(val);
                                            }, options: missingOptions, placeholder: missing.length === 0
                                                ? 'No missing benchmarks'
                                                : missingOptions.length === 0
                                                    ? 'All missing benchmarks added'
                                                    : 'Select a missing window', disabled: disabled || missingOptions.length === 0, searchable: true }), _jsx(Typography, { sx: { fontSize: 10.5, color: '#667085', mt: 0.75 }, children: "Windows where no campaign data was found." })] }), _jsxs(Box, { children: [_jsx(Dropdown, { label: `Override existing (${allOptions.length})`, value: selectedOverride, onChange: (v) => {
                                                const val = String(v ?? '');
                                                setSelectedOverride(val);
                                                if (val)
                                                    handleAddOverride(val);
                                            }, options: allOptions, placeholder: all.length === 0
                                                ? 'No windows available'
                                                : allOptions.length === 0
                                                    ? 'All windows added'
                                                    : 'Select a window to override', disabled: disabled || allOptions.length === 0, searchable: true }), _jsx(Typography, { sx: { fontSize: 10.5, color: '#667085', mt: 0.75 }, children: "Pre-fills with the current auto-calculated values." })] })] }) }), _jsxs(Box, { sx: {
                            bgcolor: '#fff',
                            border: '1px solid #EAECF0',
                            borderRadius: '8px',
                            p: 2,
                        }, children: [_jsx(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1.5,
                                }, children: _jsxs(Box, { children: [_jsxs(Typography, { sx: { fontSize: 13, fontWeight: 700, color: '#101828' }, children: ["Custom Benchmarks (", rows.length, ")"] }), _jsx(Typography, { sx: { fontSize: 11, color: '#667085', mt: 0.25 }, children: "These values override the auto-built benchmark for each window. Leave a metric blank to keep the auto value for that metric." })] }) }), _jsx(Divider, { sx: { mb: 1.5 } }), rows.length === 0 ? (_jsx(Typography, { sx: {
                                    fontSize: 12,
                                    color: '#98A2B3',
                                    textAlign: 'center',
                                    py: 4,
                                }, children: "No custom benchmarks yet. Pick a window from one of the dropdowns above." })) : (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 }, children: [_jsxs(Box, { sx: {
                                            display: 'grid',
                                            gridTemplateColumns: '220px 1fr 1fr 1fr 40px',
                                            gap: 1.5,
                                            px: 1,
                                            fontSize: 10,
                                            color: '#667085',
                                            fontWeight: 700,
                                            letterSpacing: '0.04em',
                                            textTransform: 'uppercase',
                                        }, children: [_jsx("span", { children: "Window" }), _jsx("span", { children: "OCPC ($)" }), _jsx("span", { children: "OCTR (%)" }), _jsx("span", { children: "ROAS (x)" }), _jsx("span", {})] }), rows.map((r, i) => (_jsxs(Box, { sx: {
                                            display: 'grid',
                                            gridTemplateColumns: '220px 1fr 1fr 1fr 40px',
                                            gap: 1.5,
                                            alignItems: 'center',
                                            px: 1,
                                        }, children: [_jsx(Box, { children: _jsx(Chip, { label: r.windowDays
                                                        ? `${r.windowStart} → ${r.windowEnd} (${r.windowDays}d)`
                                                        : `${r.windowStart} → ${r.windowEnd}`, size: "small", sx: {
                                                        bgcolor: r.origin === 'missing' ? '#FEE4E2' : '#FCE8F2',
                                                        color: r.origin === 'missing' ? '#B42318' : '#EE2A7B',
                                                        fontWeight: 700,
                                                        fontSize: 10.5,
                                                        height: 28,
                                                        maxWidth: 220,
                                                        '& .MuiChip-label': {
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        },
                                                    } }) }), _jsx(NumberInput, { value: r.ocpc, onChange: (e) => handleUpdateRow(i, {
                                                    ocpc: e.target.value,
                                                }), placeholder: "2.44", error: !!errors[i] }), _jsx(NumberInput, { value: r.octr, onChange: (e) => handleUpdateRow(i, {
                                                    octr: e.target.value,
                                                }), placeholder: "0.83", error: !!errors[i] }), _jsx(NumberInput, { value: r.roas, onChange: (e) => handleUpdateRow(i, {
                                                    roas: e.target.value,
                                                }), placeholder: "2.27", error: !!errors[i] }), _jsx(IconButton, { size: "small", onClick: () => handleRemoveRow(i), disabled: disabled, sx: { color: '#B42318' }, "aria-label": "Remove row", children: _jsx(DeleteOutlineIcon, { fontSize: "small" }) }), errors[i] && (_jsx(Typography, { sx: {
                                                    gridColumn: '2 / 5',
                                                    fontSize: 11,
                                                    color: '#B42318',
                                                    mt: -0.5,
                                                }, children: errors[i] }))] }, `${r.range}-${i}`)))] }))] }), disabled && (_jsx(Box, { sx: {
                            mt: 2,
                            bgcolor: '#FFF8E1',
                            border: '1px solid #F9DBAF',
                            borderRadius: '8px',
                            px: 2,
                            py: 1,
                        }, children: _jsx(Typography, { sx: { fontSize: 12, color: '#93370D' }, children: "A background job is already running for this report. Wait for it to finish before changing benchmarks." }) }))] })) }) }));
};
export default CustomBenchmarkDrawer;
